'use client'

import { useEffect, useRef, useState } from 'react'
import type { PipelineProgressStage } from '@/lib/types/pipeline'

/**
 * Backend-anchored smooth progress — refreshed correctly on page reload.
 *
 * Formula:
 *   progress = step_start_progress + (now - step_started_at) / duration_sec * (step_cap - step_start_progress)
 *
 * The backend writes `step_started_at` to `graph_state.step_started_at` when
 * the current_step changes.  Because the anchor time comes from the backend,
 * refreshing or switching pages recovers the correct progress position —
 * the bar does NOT restart from 0.
 *
 * Parameters:
 *  - realProgress:  backend progress value (0-100) for the current stage
 *  - isActive:      whether the task is still pending/running
 *  - isDone:        whether the task has reached a terminal state
 *  - stepStartedAt: backend `step_started_at` ISO string (from graph_state)
 *  - progressConfig: per-stage progress anchors with duration_sec
 *
 * Guarantees:
 *  - MONOTONIC: the displayed value NEVER decreases.
 *  - Backend real progress always wins (snap up to it, never down).
 *  - Capped at 99 until the backend reports completion.
 */

const DEFAULT_STAGE_DURATION_MS = 30 * 1000
const HARD_CAP = 99

function round1(value: number): number {
  return Math.round(value * 10) / 10
}

export function useFakeProgress(
  realProgress: number,
  isActive: boolean,
  isDone: boolean,
  stepStartedAt?: string | number | null,
  progressConfig?: PipelineProgressStage[],
): number {
  const [display, setDisplay] = useState(0)
  const floorRef = useRef(0)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isActive) {
      floorRef.current = 0
      setDisplay(0)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      return
    }

    if (isDone) {
      floorRef.current = 100
      setDisplay(100)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      return
    }

    // Parse step_started_at from backend → epoch ms
    let stepStartedAtMs = Date.now()
    if (stepStartedAt != null && stepStartedAt !== '') {
      const d = new Date(stepStartedAt)
      if (!isNaN(d.getTime())) {
        stepStartedAtMs = d.getTime()
      }
    }

    // Find the current stage from progressConfig
    const stages = progressConfig ?? []
    let stage: PipelineProgressStage | undefined
    for (const s of stages) {
      if (s.progress <= realProgress) stage = s
    }
    if (!stage && stages.length > 0) stage = stages[0]

    // stage cap = next stage's progress, or HARD_CAP
    let stageCap = HARD_CAP
    if (stage) {
      const idx = stages.indexOf(stage)
      const next = stages[idx + 1]
      if (next) stageCap = next.progress
    }

    const durationMs = stage?.duration_sec
      ? stage.duration_sec * 1000
      : DEFAULT_STAGE_DURATION_MS

    const stageStartProgress = stage?.progress ?? 0
    const range = stageCap - stageStartProgress

    const tick = () => {
      const elapsed = Date.now() - stepStartedAtMs
      const t = Math.min(elapsed / durationMs, 1)
      // Linear interpolation from stageStartProgress toward stageCap
      const value = stageStartProgress + range * t

      // Monotonic: never go below the highest value already shown
      const next = Math.max(value, floorRef.current)
      floorRef.current = next
      setDisplay(Math.min(next, HARD_CAP))

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [isActive, isDone, realProgress, stepStartedAt, progressConfig])

  // Real backend progress — snap up to it, but never drop below floorRef
  if (isDone) return 100
  if (realProgress > floorRef.current) {
    floorRef.current = realProgress
    setDisplay(realProgress) // sync display state so fallback doesn't regress
    return round1(realProgress)
  }
  // Monotonic guarantee: never drop below the highest value shown
  return round1(Math.min(Math.max(display, floorRef.current), HARD_CAP))
}